import {
  AppBar,
  Drawer,
  IconButton,
  Stack,
  SvgIcon,
  SxProps,
  Toolbar,
  Typography,
} from '@mui/material';
import throttle from 'lodash/throttle';
import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { NavItems } from './nav-items';
import { MobileNavCloseIcon } from '../../config/icons';
import { BACKDROP_STYLE } from '../../config/styles';
import { TNavbarPage } from '../../types/admin';

export interface NavbarProps {
  navbarPages: TNavbarPage[];
  activeKey?: string;
  onNavItemClick: (page: TNavbarPage) => void;
  backButton?: ReactNode;
  shareButton?: ReactNode;
  t: (key: string) => string;
  direction?: 'ltr' | 'rtl';
  isLandingPreview?: boolean;
  isAdminPreview?: boolean;
  isMobile?: boolean;
  onScroll?: (activeKey: string) => void;
  sectionIdPrefix?: string;
  zIndexNavbar?: number;
  zIndexDrawer?: number;
  sx?: SxProps;
  isLandingYzer?: boolean;
}

export function Navbar({
  navbarPages,
  activeKey,
  onNavItemClick,
  backButton,
  shareButton,
  t,
  direction = 'ltr',
  isLandingPreview = false,
  isAdminPreview = false,
  isMobile = false,
  onScroll,
  sectionIdPrefix = 'section-',
  zIndexNavbar = 1000,
  zIndexDrawer = 10,
  sx = {},
  isLandingYzer = false,
}: NavbarProps) {
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState<boolean>(false);
  const skipScrollHandle = useRef<boolean>(false);

  const closeNavDrawer = () => {
    setIsNavDrawerOpen(false);
  };

  // Smooth scroll to a container when a navbar item is clicked
  const scrollToContainer = (page: TNavbarPage) => {
    const id = isAdminPreview ? page.previewSectionsKey || page.key : page.key;
    const targetElement = document.querySelector(`#${sectionIdPrefix}${id}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // close drawer on mobile
      if (isMobile) {
        closeNavDrawer();
      }
    }
  };

  // Function to handle scroll and update active index
  const handleScroll = useCallback(() => {
    if (!onScroll || skipScrollHandle.current) return;

    const sections = Array.from(document.querySelectorAll(`[id^="${sectionIdPrefix}"]`));
    let newActiveIndex = 0;

    sections.forEach((section, index) => {
      const sectionRect = section.getBoundingClientRect();
      // -300 is to make the active tab change a bit later (upper half of the screen)
      if (sectionRect.top <= window.innerHeight / 2 - 300) {
        newActiveIndex = index;
      }
    });

    const activeSection = sections[newActiveIndex];
    const activeTabKey = activeSection?.id.split('-')[1];

    if (activeTabKey) {
      onScroll(activeTabKey);
    }
  }, [onScroll, sectionIdPrefix]);

  const handleNavItemClick = (page: TNavbarPage) => {
    scrollToContainer(page);
    onNavItemClick(page);

    // dont handle on-scroll item highlight for 1 sec.
    skipScrollHandle.current = true;
    setTimeout(() => {
      skipScrollHandle.current = false;
    }, 1000);
  };

  const throttledHandleScroll = useMemo(() => throttle(handleScroll, 100), [handleScroll]);

  useEffect(() => {
    if (onScroll) {
      window.addEventListener('scroll', throttledHandleScroll, { capture: true });

      return () => {
        window.removeEventListener('scroll', throttledHandleScroll);
        throttledHandleScroll.cancel();
      };
    }
  }, [throttledHandleScroll, onScroll]);

  return (
    <AppBar
      position="sticky"
      sx={{
        boxShadow: (theme) => ({ xs: theme.shadows[1], sm: theme.shadows[2] }),
        zIndex: zIndexNavbar,
        top: isLandingPreview ? (isMobile ? '40px' : '50px') : 0,
        ...sx,
      }}
      dir={direction}
    >
      <Toolbar
        sx={{
          minHeight: 'unset',
          padding: { xs: '10px 15px', sm: '10px 20px' },
          background: '#fff',
          overflow: 'hidden',
        }}
      >
        {!isMobile ? (
          <>
            <Stack direction="row" gap="10px" alignItems="center">
              {backButton}
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="center" flexGrow={1}>
              {!isLandingYzer && (
                <NavItems
                  onClick={handleNavItemClick}
                  navbarPages={navbarPages}
                  activeKey={activeKey}
                  isAdminPreview={isAdminPreview}
                  direction={direction}
                />
              )}
            </Stack>

            <Stack direction="row" gap="10px" alignItems="center">
              {shareButton}
            </Stack>
          </>
        ) : (
          <>
            <Stack
              direction="row"
              gap="10px"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              {backButton}
              <div />
              <Stack direction="row" spacing="10px">
                {shareButton}
              </Stack>
            </Stack>

            <Drawer
              anchor={direction === 'rtl' ? 'left' : 'right'}
              open={isNavDrawerOpen}
              onClose={closeNavDrawer}
              ModalProps={{ keepMounted: true }}
              data-treedistestid="drawer-mobile-navigation"
              sx={{
                zIndex: zIndexNavbar + zIndexDrawer,
                '& .MuiPaper-root': {
                  width: '300px',
                  backgroundColor: 'white',
                },
                '& .MuiBackdrop-root': BACKDROP_STYLE,
              }}
            >
              <Stack direction="column">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    padding: '10px 20px',
                    borderBottom: '1px solid #ECECEC',
                  }}
                >
                  <Typography variant="h6">{t('labels.menu')}</Typography>
                  <IconButton
                    onClick={closeNavDrawer}
                    data-treedistestid="button-mobile-nav-close"
                    sx={{
                      width: '32px',
                      height: '32px',
                      padding: '0px',
                      border: 'none',
                      boxShadow: 'none',
                    }}
                  >
                    <SvgIcon>{MobileNavCloseIcon}</SvgIcon>
                  </IconButton>
                </Stack>

                {!isLandingYzer && (
                  <NavItems
                    onClick={handleNavItemClick}
                    navbarPages={navbarPages}
                    activeKey={activeKey}
                    direction={direction}
                    containerStyles={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: '10px 20px',
                      gap: '12px',
                    }}
                    itemStyles={{
                      lineHeight: '34px',
                    }}
                  />
                )}
              </Stack>
            </Drawer>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}
